'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { getWhatsAppUrl } from '../../../../config/contact';

export default function HeroSection() {
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div id="hero" className="relative isolate overflow-hidden bg-gradient-to-br from-background-100 via-primary-50 to-background-50">
      {/* Enhanced Background gradient effects */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 opacity-70">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/3 transform sm:translate-x-1/4 lg:translate-x-1/6 xl:translate-x-1/8">
          <div className="h-[400px] w-[400px] sm:h-[500px] sm:w-[500px] lg:h-[700px] lg:w-[700px] rounded-full bg-gradient-to-tr from-primary-200/70 via-primary-100/50 to-accent-100/30 opacity-40 blur-3xl animate-pulse animation-delay-2000"></div>
        </div>
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 transform lg:translate-y-1/4">
          <div className="h-[350px] w-[350px] sm:h-[450px] sm:w-[450px] rounded-full bg-gradient-to-bl from-accent-200/70 via-accent-100/50 to-primary-100/30 opacity-40 blur-3xl animate-pulse"></div>
        </div>
      </div>

      {/* Grid pattern background - subtle */}
      <svg
        className="absolute inset-0 -z-20 h-full w-full stroke-primary-100/40 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="hero-pattern"
            width={150} // Slightly smaller pattern
            height={150}
            x="50%"
            y={-1}
            patternUnits="userSpaceOnUse"
          >
            <path d="M75 150V.5M.5 .5H150" fill="none" strokeDasharray="2 2" /> {/* Dashed lines for subtlety */}
          </pattern>
        </defs>
        <rect width="100%" height="100%" strokeWidth={0} fill="url(#hero-pattern)" />
      </svg>

      {/* Main content */}
      <div className="relative mx-auto max-w-7xl px-6 pt-28 pb-20 lg:flex lg:items-center lg:gap-x-12 lg:px-8 lg:pt-40 lg:pb-28">
        {/* Left side - Text content */}
        <div className="mx-auto max-w-2xl lg:mx-0 lg:flex-auto">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 25 }}
            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute -left-10 top-1/2 h-28 w-28 -translate-y-1/2 -translate-x-1/2 transform rounded-full bg-primary-200/50 blur-3xl opacity-80"></div>
            <h1 className="mt-10 font-display font-bold tracking-tight text-text-700">
              {/* First line - always stays as one line */}
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
                Turn Menu Photos
              </span>
              
              {/* Second line with responsive underline */}
              <span className="mt-1 sm:mt-2 block text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
                <span className="relative inline-flex flex-col sm:flex-row items-start sm:items-center">
                  <span className="relative mr-2 sm:mr-3">into</span>
                  <span className="relative">
                    Digital
                    {/* Mobile underline - gradient border instead of SVG */}
                    <span className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-highlight-400/80 via-accent-300/80 to-highlight-400/80 sm:hidden"></span>
                    
                    {/* Tablet/Desktop underline - SVG with proper scaling */}
                    <svg 
                      aria-hidden="true" 
                      viewBox="0 0 418 42" 
                      className="absolute hidden sm:block left-0 bottom-[-0.2em] h-[0.3em] sm:h-[0.4em] md:h-[0.5em] w-full" 
                      preserveAspectRatio="none"
                    >
                      <motion.path 
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: isLoaded ? 1 : 0 }}
                        transition={{ delay: 0.4, duration: 0.8, ease: "easeInOut" }}
                        className="fill-highlight-400/80"
                        d="M203.371.916c-26.013-2.078-76.686 1.963-124.73 9.946L67.3 12.749C35.421 18.062 18.2 21.766 6.004 25.934 1.244 27.561.828 27.778.874 28.61c.07 1.214.828 1.121 9.595-1.176 9.072-2.377 17.15-3.92 39.246-7.496C123.565 7.986 157.869 4.492 195.942 5.046c7.461.108 19.25 1.696 19.17 2.582-.107 1.183-7.874 4.31-25.75 10.366-21.992 7.45-35.43 12.534-36.701 13.884-2.173 2.308-.202 4.407 4.442 4.734 2.654.187 3.263.157 15.593-.78 35.401-2.686 57.944-3.488 88.365-3.143 46.327.526 75.721 2.23 130.788 7.584 19.787 1.924 20.814 1.98 24.557 1.332l.066-.011c1.201-.203 1.53-1.825.399-2.335-2.911-1.31-4.893-1.604-22.048-3.261-57.509-5.556-87.871-7.36-132.059-7.842-23.239-.254-33.617-.116-50.627.674-11.629.54-42.371 2.494-46.696 2.967-2.359.259 8.133-3.625 26.504-9.81 23.239-7.825 27.934-10.149 28.304-14.005.417-4.348-3.529-6-16.878-7.066Z">
                      </motion.path>
                    </svg>
                  </span>
                </span>
              </span>
              
              {/* Third line with gradient text - added padding and fixed line height */}
              <motion.span 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 10 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mt-1 sm:mt-2 block text-5xl sm:text-6xl md:text-7xl lg:text-8xl bg-gradient-to-r from-primary-500 via-primary-600 to-primary-500 bg-clip-text text-transparent font-extrabold leading-tight pb-2"
              >
                Magic
              </motion.span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 25 }}
            transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
            className="mt-8 text-lg font-medium text-pretty text-text-500 leading-8"
          >
            Turn your old menu card into a smart digital menu.
            <span className="block mt-1 bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent font-semibold"> Searchable. Filterable. Instant Updates.</span> All behind one simple QR.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 25 }}
            transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
            className="mt-12 flex items-center gap-x-6"
          >
            <a
              href={getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl bg-accent-500 px-7 py-3.5 text-base font-semibold text-white shadow-lg interactive-transition hover:bg-accent-600 hover:shadow-accent-500/60 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600"
            >
              Request Setup on WhatsApp
            </a>
            <a href="#how-it-works" className="text-base font-semibold text-text-600 interactive-transition hover:text-primary-600 group flex items-center">
               Learn more <span aria-hidden="true" className="ml-1.5 transform group-hover:translate-x-1 interactive-transition">→</span>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 25 }}
            transition={{ duration: 0.7, delay: 0.7, ease: "easeOut" }}
            className="mt-10 sm:mt-14 flex items-center justify-start"
          >
            <div className="flex">
              <div className="relative rounded-full px-5 py-2 text-sm leading-6 text-text-500 ring-1 ring-text-300/30 hover:ring-primary-400/50 interactive-transition bg-white/30 backdrop-blur-sm">
                No app downloads <span className="mx-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary-400 opacity-70"></span> No hidden charges <span className="mx-1.5 inline-block h-1.5 w-1.5 rounded-full bg-accent-400 opacity-70"></span> Cancel Anytime
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right side - Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: isLoaded ? 1 : 0, scale: isLoaded ? 1 : 0.9, y: isLoaded ? 0 : 20 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          className="mt-20 sm:mt-28 lg:mt-0 lg:flex-shrink-0 lg:flex-grow relative"
        >
          <div className="relative mx-auto h-[600px] w-full max-w-md"> {/* Adjusted max-w */}
            {/* Decorative elements around phone */}
            <div className="absolute -right-10 -top-10 h-px w-px">
                <div className="absolute h-36 w-36 -translate-x-1/2 transform rounded-full bg-gradient-to-br from-primary-500/60 to-primary-400/40 opacity-30 blur-2xl animate-pulse animation-delay-1000"></div>
            </div>
            <div className="absolute -bottom-16 -left-10 h-px w-px">
              <div className="absolute h-44 w-44 -translate-x-1/2 transform rounded-full bg-gradient-to-tr from-accent-500/60 to-highlight-400/40 opacity-30 blur-2xl animate-pulse"></div>
            </div>

            {/* Device mockup */}
            <div className="relative mx-auto group">
              {/* Phone frame */}
              <div className="relative mx-auto h-[620px] w-[310px] overflow-hidden rounded-[2.75rem] border-[10px] border-text-800 bg-text-900 shadow-2xl transform group-hover:scale-[1.02] interactive-transition group-hover:shadow-primary-500/30">
                <div className="absolute left-1/2 top-0 h-7 w-44 -translate-x-1/2 transform rounded-b-lg bg-text-800"></div>
                <div className="absolute left-1/2 top-2.5 h-1.5 w-20 -translate-x-1/2 transform rounded-lg bg-text-700"></div>
                
                {/* Menu screen content */}
                <div className="h-full w-full bg-white overflow-hidden">
                  <div className="flex items-center justify-between bg-gradient-to-r from-primary-600 to-primary-500 px-4 pt-12 pb-4">
                    <div className="flex items-center">
                      <div className="text-sm font-semibold text-white">Bangalore Spice</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-xs font-medium text-white/80">Our Menu</div>
                    </div>
                  </div>
                  
                  <div className="relative h-[calc(100%-4rem)] bg-background-50 px-3.5 py-3 overflow-y-auto scrollbar-thin scrollbar-thumb-primary-300 scrollbar-track-primary-100">
                    <div className="sticky top-0 z-10 mb-3.5 flex h-11 items-center rounded-full bg-white px-4 py-1.5 shadow-md ring-1 ring-text-300/10">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4.5 w-4.5 text-primary-500">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                      </svg>
                      <span className="ml-2.5 text-sm text-text-400">Search our menu...</span>
                    </div>
                    
                    <div className="mt-5 flex flex-wrap gap-2.5">
                       <div className="rounded-full bg-primary-100 px-3.5 py-1.5 text-xs font-medium text-primary-700 ring-1 ring-primary-300/50">Veg Only</div>
                      <div className="rounded-full bg-background-200 px-3.5 py-1.5 text-xs font-medium text-text-600 ring-1 ring-text-300/30">Under ₹200</div>
                      <div className="rounded-full bg-background-200 px-3.5 py-1.5 text-xs font-medium text-text-600 ring-1 ring-text-300/30">Spicy</div>
                    </div>
                    
                    <div className="mb-2.5 mt-5">
                      <h3 className="text-base font-bold text-text-700">Popular Items</h3>
                    </div>
                    
                    <div className="space-y-3.5">
                      {[
                        { name: "Butter Chicken", desc: "Tender chicken in creamy tomato gravy", price: "₹250" },
                        { name: "Paneer Tikka", desc: "Marinated cottage cheese, grilled", price: "₹180" },
                        { name: "Masala Dosa", desc: "Crispy crepe, spiced potato filling", price: "₹99" },
                      ].map(item => (
                        <div key={item.name} className="rounded-lg bg-white p-3.5 shadow-sm ring-1 ring-text-300/10 interactive-transition hover:ring-primary-400/50 hover:shadow-md">
                          <div className="flex justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-text-700 group-hover:text-primary-600 interactive-transition">{item.name}</h4>
                              <p className="mt-1 text-xs text-text-500">{item.desc}</p>
                            </div>
                            <div className="text-sm font-semibold text-primary-600">{item.price}</div>
                          </div>
                        </div>
                      ))}
                       <div className="mb-2.5 mt-5">
                        <h3 className="text-base font-bold text-text-700">Beverages</h3>
                      </div>
                       <div className="rounded-lg bg-white p-3.5 shadow-sm ring-1 ring-text-300/10 interactive-transition hover:ring-primary-400/50 hover:shadow-md">
                          <div className="flex justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-text-700 group-hover:text-primary-600 interactive-transition">Filter Coffee</h4>
                              <p className="mt-1 text-xs text-text-500">Traditional South Indian coffee</p>
                            </div>
                            <div className="text-sm font-semibold text-primary-600">₹40</div>
                          </div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* QR Code with enhanced styling */}
              <div className="absolute -right-12 -top-12 rotate-12 rounded-xl bg-gradient-to-br from-primary-500 via-accent-500 to-highlight-400 p-2 shadow-xl interactive-transition group-hover:scale-110 group-hover:rotate-[15deg]">
                <div className="h-28 w-28 rounded-md bg-white p-1.5">
                  <div className="h-full w-full bg-[url('/qr-placeholder.png')] bg-cover bg-center rounded-sm"></div>
                </div>
                <div className="absolute -right-2.5 -top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-accent-500 text-[11px] font-bold text-white shadow-md ring-2 ring-white">QR</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Down arrow indicator - slightly more prominent */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : -15 }}
        transition={{ duration: 0.6, delay: 1, repeat: Infinity, repeatType: "mirror", repeatDelay: 0.3, ease: "easeInOut" }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 transform z-10"
      >
        <a href="#why-scanmaadi" className="flex flex-col items-center group cursor-pointer">
          <span className="text-xs text-text-500 group-hover:text-primary-500 interactive-transition">Scroll to explore</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="mt-1.5 h-5 w-5 text-text-400 group-hover:text-primary-500 interactive-transition transform group-hover:scale-110">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
          </svg>
        </a>
      </motion.div>

      {/* Subtle decorative shape for transition to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-20  bg-gradient-to-t from-background-100 to-transparent z-0 opacity-50" style={{clipPath: 'ellipse(100% 50% at 50% 100%)'}}></div>

    </div>
  );
}